import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAVIGATION } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Check if we're on the homepage hero (dark background)
  const isHomepage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Determine text color based on scroll state and page
  const getTextColor = () => {
    if (isScrolled || !isHomepage) {
      return 'var(--velora-green-deep)';
    }
    return 'var(--velora-champagne)';
  };

  const getHoverColor = () => {
    if (isScrolled || !isHomepage) {
      return 'var(--velora-gold)';
    }
    return 'var(--velora-gold-light)';
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled
          ? 'py-4'
          : 'py-6'
      }`}
      style={{
        background: isScrolled 
          ? 'rgba(250, 248, 243, 0.97)' 
          : 'transparent',
        backdropFilter: isScrolled ? 'blur(12px)' : 'none',
        boxShadow: isScrolled ? '0 1px 0 rgba(196, 169, 98, 0.1)' : 'none',
      }}
    >
      <div className="container-luxury flex items-center justify-between">
        {/* Logo as Typography */}
        <Link 
          to="/" 
          className="relative z-50 font-display text-2xl font-medium tracking-tight transition-colors duration-400"
          style={{ color: getTextColor() }}
        >
          Velora
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-14">
          {NAVIGATION.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className="label-sm tracking-widest transition-colors duration-400"
              style={{
                color: location.pathname === item.path 
                  ? getHoverColor() 
                  : getTextColor(),
              }}
              onMouseEnter={(e) => e.target.style.color = getHoverColor()}
              onMouseLeave={(e) => {
                e.target.style.color = location.pathname === item.path 
                  ? getHoverColor() 
                  : getTextColor();
              }}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden relative z-50 p-2 transition-colors duration-400"
          aria-label="Toggle menu"
          style={{ color: isMobileMenuOpen ? 'var(--velora-green-deep)' : getTextColor() }}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Menu */}
        <div
          className={`fixed inset-0 z-40 md:hidden transition-all duration-600 ${
            isMobileMenuOpen 
              ? 'opacity-100 visible' 
              : 'opacity-0 invisible'
          }`}
          style={{ background: 'var(--velora-ivory)' }}
        >
          <nav className="flex flex-col items-center justify-center h-full space-y-10">
            {NAVIGATION.map((item, index) => (
              <Link
                key={item.name}
                to={item.path}
                className={`font-display text-3xl font-light transition-all duration-500 ${
                  isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: `${index * 80}ms`,
                  color: location.pathname === item.path
                    ? 'var(--velora-gold)'
                    : 'var(--velora-green-deep)',
                }}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
